/**
 * Saisir un ordre de réparation, devant la machine.
 *
 * *Demande de Chris, 17/08/2026 : « un petit module simple où j'ouvre l'appli et le menu saisir
 * un OR, je peux prendre la photo du QR sur la machine et cela doit préremplir tout le formulaire
 * avec toutes les données présentes récupérées. »*
 *
 * ## Pourquoi une PHOTO et non un flux vidéo
 *
 * `<input type="file" capture="environment">` ouvre l'appareil photo du téléphone et rend un
 * fichier. C'est exactement le geste demandé — « prendre la photo du QR » —, et c'est aussi le
 * seul qui marche partout : un flux vidéo (`getUserMedia` + `BarcodeDetector`) demande une
 * permission caméra persistante, et `BarcodeDetector` **n'existe pas sur Safari iOS**. Un module
 * qui ne fonctionnerait pas sur l'iPhone d'un chef d'équipe ne serait pas un module.
 *
 * Sur un ordinateur, le même bouton ouvre un sélecteur de fichier : de quoi essayer avec une
 * capture d'écran de l'étiquette, sans téléphone.
 *
 * ## Le domaine du QR est jeté, et c'est ce qui évite de réimprimer 229 étiquettes
 *
 * L'étiquette encode une adresse du **référentiel maître**. Scannée avec l'appareil photo natif,
 * elle ouvrirait ce référentiel — où un mécano d'atelier n'a pas de compte. Ici, le texte lu part
 * au serveur qui en extrait le code ERP et **ignore le domaine** : les étiquettes déjà produites
 * servent telles quelles.
 *
 * ## La saisie à la main n'est pas un ornement
 *
 * Une étiquette s'abîme, se couvre de boue, se décolle. Le champ de code reste donc toujours
 * accessible, et il passe par la **même** route que le scan : un code tapé se comporte exactement
 * comme un code lu.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { lireQrDansImage } from "./scan";
import {
  chargerChantiers,
  chargerDroitsIncident,
  chargerOrdres,
  chargerPhotosMachine,
  chargerTypesIncident,
  chercherMachine,
  chercherMachines,
  creerOrdre,
  joindrePhotoIncident,
} from "./api";
import type { FicheMachine, MachineTrouvee, OrdreReparation, TypeIncident } from "./api";
import type { ChantierListe } from "@planif/moteur";

/** Les champs de la fiche, dans l'ordre où on les lit debout devant la machine. */
const CHAMPS: readonly { readonly cle: keyof FicheMachine; readonly libelle: string }[] = [
  { cle: "description", libelle: "Description" },
  { cle: "type", libelle: "Type" },
  { cle: "immatriculation", libelle: "Immatriculation" },
  { cle: "numSerie", libelle: "N° de série" },
  { cle: "generique", libelle: "Code générique" },
  { cle: "categorie", libelle: "Catégorie" },
  { cle: "societeProprietaire", libelle: "Propriétaire" },
  { cle: "societe", libelle: "Société" },
  { cle: "fournisseur", libelle: "Fournisseur" },
  { cle: "etatParc", libelle: "État parc" },
  { cle: "etatAchat", libelle: "État à l'achat" },
  { cle: "affectation", libelle: "Affectation" },
  { cle: "poids", libelle: "Poids" },
  { cle: "essieux", libelle: "Essieux" },
  { cle: "dateEntree", libelle: "Entrée au parc" },
  { cle: "dateSortie", libelle: "Sortie du parc" },
];

/**
 * Lit un QR dans une image, en deux passes.
 *
 * Rendre `null` plutôt que lever : « je n'ai pas su lire » n'est pas une panne, c'est le cas
 * courant d'une photo floue ou prise de trop loin. Le message qui suit doit dire quoi refaire,
 * pas afficher une trace technique.
 */
type Etat =
  | { readonly phase: "attente" }
  | { readonly phase: "lecture" }
  | { readonly phase: "trouvee"; readonly machine: FicheMachine; readonly scan: string }
  | { readonly phase: "erreur"; readonly raison: string };

export function OrdreReparation(): JSX.Element {
  const [etat, setEtat] = useState<Etat>({ phase: "attente" });
  const [code, setCode] = useState("");
  const [constat, setConstat] = useState("");
  const [typeCode, setTypeCode] = useState("");
  const [types, setTypes] = useState<readonly TypeIncident[]>([]);
  const [chantiers, setChantiers] = useState<readonly ChantierListe[]>([]);
  const [chantierId, setChantierId] = useState("");
  const [horsChantier, setHorsChantier] = useState(false);
  /**
   * « La machine est-elle immobilisée ? » — trois états et non deux.
   *
   * `null` est « pas encore répondu », et il est le départ. Un bouton posé sur « non » par défaut
   * ferait enregistrer une réponse que personne n'a donnée — et c'est sur cette réponse que le
   * responsable qualifie.
   */
  const [immobilisee, setImmobilisee] = useState<boolean | null>(null);
  /** Le compteur au moment de l incident (091). Facultatif : une panne ne doit pas rester
   *  non signalee parce que le cadran est illisible. */
  const [compteur, setCompteur] = useState("");
  const [compteurUnite, setCompteurUnite] = useState<"km" | "heures">("km");
  /** Les photos du DÉGÂT, choisies avant l'envoi et jointes juste après. */
  const [photosDegat, setPhotosDegat] = useState<readonly File[]>([]);
  const [peutDeclarer, setPeutDeclarer] = useState(true);
  const [motifRefus, setMotifRefus] = useState<string | null>(null);
  /** Ce que la frappe en cours propose, et si la liste est coupée. */
  const [propositions, setPropositions] = useState<readonly MachineTrouvee[]>([]);
  const [tronque, setTronque] = useState(false);
  /** Vrai seulement quand une recherche a VRAIMENT abouti à rien — pas avant d'avoir cherché. */
  const [aucuneProposition, setAucuneProposition] = useState(false);
  /**
   * Le numéro de la dernière frappe partie.
   *
   * Deux réponses peuvent revenir dans le désordre — « pel » après « pelle » —, et la plus
   * ancienne écraserait alors la plus récente : la liste afficherait le résultat d'un texte que
   * l'utilisateur ne voit plus dans son champ. Seule la dernière demande a le droit d'écrire.
   */
  const frappe = useRef(0);
  const [photos, setPhotos] = useState<readonly string[]>([]);
  const [messagePhotos, setMessagePhotos] = useState<string | null>(null);
  const [ordres, setOrdres] = useState<readonly OrdreReparation[]>([]);
  const [envoi, setEnvoi] = useState(false);
  const [erreurEnvoi, setErreurEnvoi] = useState<string | null>(null);
  const [enregistre, setEnregistre] = useState<OrdreReparation | null>(null);
  const champFichier = useRef<HTMLInputElement>(null);

  const rechargerOrdres = useCallback((): void => {
    chargerOrdres()
      .then((r) => {
        setOrdres(r.ordres);
      })
      .catch(() => {
        // La liste des derniers ordres est un confort : son échec ne doit pas empêcher de saisir.
      });
  }, []);

  useEffect(rechargerOrdres, [rechargerOrdres]);

  /*
    Les types, les chantiers et le droit de déclarer, chargés une fois.

    Leur échec n'est pas silencieux mais il n'est pas bloquant non plus : sans la liste des types
    le bouton reste inactif et le dit, ce qui vaut mieux qu'un formulaire qui s'envoie et se fait
    refuser.
  */
  useEffect(() => {
    chargerTypesIncident()
      .then((r) => {
        setTypes(r.types.filter((t) => t.actif));
      })
      .catch(() => {
        setTypes([]);
      });
    chargerChantiers()
      .then((r) => {
        setChantiers(r.chantiers);
      })
      .catch(() => {
        setChantiers([]);
      });
    chargerDroitsIncident()
      .then((r) => {
        setPeutDeclarer(r.peutDeclarer);
        setMotifRefus(r.motif);
      })
      .catch(() => {
        // Le droit se vérifie de toute façon à l'écriture : ne pas fermer l'écran sur un doute.
      });
  }, []);

  /** Cherche la machine, à partir d'un scan ou d'un code tapé — le même chemin pour les deux. */
  const chercher = useCallback((scan: string): void => {
    setEtat({ phase: "lecture" });
    setPhotos([]);
    setMessagePhotos(null);
    setEnregistre(null);
    setErreurEnvoi(null);
    chercherMachine(scan)
      .then(({ machine, code: trouve }) => {
        setEtat({ phase: "trouvee", machine, scan });
        setCode(trouve);
        if (!machine.aPhoto1 && !machine.aPhoto2) return;
        return chargerPhotosMachine(machine.id)
          .then((r) => {
            setPhotos(Object.values(r.photos));
            setMessagePhotos(r.message);
          })
          .catch(() => {
            setMessagePhotos("Les photos n'ont pas pu être chargées.");
          });
      })
      .catch((x: unknown) => {
        setEtat({ phase: "erreur", raison: x instanceof Error ? x.message : String(x) });
      });
  }, []);

  /**
   * Propose les machines qui correspondent à ce qui vient d'être tapé.
   *
   * Pas de temporisation : la recherche est un filtre sur les 227 machines déjà en cache côté
   * serveur, donc la réponse revient en quelques millisecondes. Ajouter un délai rendrait la liste
   * en retard d'une lettre sur la frappe, ce qui se lit comme une application lente.
   *
   * L'échec est **silencieux** ici, et c'est délibéré : une proposition qui ne vient pas laisse le
   * champ fonctionner comme avant — on tape le code entier et on valide. Une alerte rouge sous un
   * champ de recherche ferait croire que la déclaration est impossible.
   */
  const proposer = useCallback((saisi: string): void => {
    const mien = ++frappe.current;
    const texte = saisi.trim();
    if (texte.length < 2) {
      setPropositions([]);
      setTronque(false);
      setAucuneProposition(false);
      return;
    }
    chercherMachines(texte)
      .then((r) => {
        if (frappe.current !== mien) return;
        setPropositions(r.machines);
        setTronque(r.tronque);
        setAucuneProposition(r.machines.length === 0);
      })
      .catch(() => {
        if (frappe.current !== mien) return;
        setPropositions([]);
        setTronque(false);
        setAucuneProposition(false);
      });
  }, []);

  const surPhoto = (fichier: File | undefined): void => {
    if (fichier === undefined) return;
    setEtat({ phase: "lecture" });
    lireQrDansImage(fichier)
      .then((texte) => {
        if (texte === null) {
          setEtat({
            phase: "erreur",
            raison:
              "Aucun QR lu sur la photo. Cadrez l'étiquette de plus près, à plat et bien " +
              "éclairée — ou saisissez le code imprimé sous le QR.",
          });
          return;
        }
        chercher(texte);
      })
      .catch(() => {
        setEtat({
          phase: "erreur",
          raison: "Cette image n'a pas pu être ouverte. Reprenez la photo.",
        });
      })
      .finally(() => {
        // Sans cette remise à zéro, reprendre DEUX fois la même machine ne déclenche rien :
        // l'input ne signale pas de changement si le fichier choisi est identique.
        if (champFichier.current !== null) champFichier.current.value = "";
      });
  };

  /**
   * Enregistre la remontée, puis joint ses photos.
   *
   * Dans cet ordre, parce que les photos se rattachent à une remontée qui doit exister. Une photo
   * qui échoue **ne perd pas la déclaration** : celle-ci est enregistrée, l'écran dit ce qui n'a
   * pas pu être joint, et l'atelier a au moins le constat. L'inverse — tout annuler parce qu'une
   * image de 12 Mo n'est pas passée — perdrait le signalement, ce que le module existe pour éviter.
   */
  const enregistrer = (): void => {
    if (etat.phase !== "trouvee") return;
    if (immobilisee === null) return;
    setEnvoi(true);
    setErreurEnvoi(null);
    creerOrdre({
      scan: etat.scan,
      constat,
      typeCode,
      chantierId: horsChantier ? undefined : chantierId,
      horsChantier,
      immobiliseeConstatee: immobilisee,
      compteur: compteur.trim() === "" ? undefined : compteur.trim(),
      compteurUnite: compteur.trim() === "" ? undefined : compteurUnite,
    })
      .then(async ({ ordre }) => {
        const manquees: string[] = [];
        for (const fichier of photosDegat) {
          try {
            await joindrePhotoIncident(ordre.id, fichier);
          } catch (x: unknown) {
            manquees.push(x instanceof Error ? x.message : String(x));
          }
        }
        setEnregistre(ordre);
        if (manquees.length > 0) {
          setErreurEnvoi(
            `La remontée n° ${String(ordre.numero)} est enregistrée, mais ` +
              `${String(manquees.length)} photo(s) n'ont pas pu être jointes : ${manquees[0] ?? ""}`,
          );
        }
        setConstat("");
        setTypeCode("");
        setChantierId("");
        setHorsChantier(false);
        setImmobilisee(null);
        setCompteur("");
        setPhotosDegat([]);
        setEtat({ phase: "attente" });
        setPhotos([]);
        rechargerOrdres();
      })
      .catch((x: unknown) => {
        setErreurEnvoi(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setEnvoi(false);
      });
  };

  /**
   * Ce qui empêche encore d'envoyer, en une phrase — et rien quand tout est là.
   *
   * Un bouton grisé sans explication se lit comme une panne de l'application. Celui-ci dit ce
   * qu'il attend, ce qui est la seule façon de ne pas laisser quelqu'un chercher.
   */
  const ceQuiManque = ((): string | null => {
    if (!peutDeclarer) return motifRefus;
    if (typeCode === "") return "Choisissez le type d'incident.";
    if (constat.trim() === "") return "Décrivez ce qui ne va pas.";
    if (!horsChantier && chantierId === "") {
      return "Dites où c'est arrivé : le chantier, ou « à l'atelier, hors chantier ».";
    }
    if (immobilisee === null) return "Répondez « la machine est-elle immobilisée ? ».";
    if (photosDegat.length === 0) return "Joignez au moins une photo du dégât.";
    return null;
  })();

  return (
    <section className="or">
      <p className="note">
        Photographiez l'étiquette QR collée sur la machine : sa fiche est lue au référentiel du
        groupe et le formulaire se remplit tout seul. Si l'étiquette est abîmée, saisissez le code
        imprimé dessous.
      </p>

      <div className="or__lecture">
        <label className="or__photo">
          {/* `capture="environment"` demande l'appareil photo ARRIÈRE. Sans lui, certains
              téléphones ouvrent la caméra frontale, avec laquelle on ne photographie pas une
              étiquette collée sur un engin. */}
          <input
            ref={champFichier}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => {
              surPhoto(e.target.files?.[0]);
            }}
          />
          <span>📷 Photographier l'étiquette</span>
        </label>

        {/*
          **La recherche propose au fur et à mesure, par code OU par libellé** (Chris,
          21/09/2026).

          Le champ n'acceptait qu'un code ERP complet et exact. C'était juste quand l'étiquette est
          lisible, et inutilisable dans le seul cas où la saisie à la main existe : étiquette
          couverte de boue, décollée, ou personne qui sait seulement que c'est « la pelle de 20 t ».

          Le formulaire reste, et son bouton aussi : taper un code entier puis « Entrée » doit
          continuer de marcher — c'est le geste de quelqu'un qui lit l'étiquette à l'œil, et le
          changer aurait cassé une habitude pour en installer une autre.
        */}
        <form
          className="or__code"
          onSubmit={(e) => {
            e.preventDefault();
            if (code.trim() !== "") chercher(code.trim());
          }}
        >
          <input
            type="text"
            value={code}
            placeholder="le code, ou « pelle », « 3086 »…"
            autoComplete="off"
            onChange={(e) => {
              setCode(e.target.value);
              proposer(e.target.value);
            }}
          />
          <button type="submit" disabled={code.trim() === ""}>
            Chercher
          </button>
        </form>
      </div>

      {/*
        La liste des propositions, sous le champ.

        Elle ne s'affiche que tant qu'aucune fiche n'est ouverte : une fois la machine choisie,
        elle n'a plus rien à proposer et occuperait la place du formulaire de déclaration.
      */}
      {etat.phase !== "trouvee" && propositions.length > 0 && (
        <ul className="or__propositions">
          {propositions.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                onClick={() => {
                  setCode(m.codeErp);
                  setPropositions([]);
                  chercher(m.codeErp);
                }}
              >
                <code>{m.codeErp}</code>
                <span>
                  {m.description ?? m.type ?? "—"}
                  {m.immatriculation === null ? "" : ` · ${m.immatriculation}`}
                  {/* Une machine sortie du parc est proposée, jamais masquée : c'est souvent
                      celle qu'on cherche. Mais on le DIT (règle de la 031). */}
                  {m.horsParc && <strong> · hors parc</strong>}
                </span>
              </button>
            </li>
          ))}
          {tronque && (
            <li className="note">
              Seules les {propositions.length} premières sont affichées : précisez la recherche.
            </li>
          )}
        </ul>
      )}
      {etat.phase !== "trouvee" && aucuneProposition && (
        <p className="note">Aucune machine ne correspond à « {code.trim()} ».</p>
      )}

      {etat.phase === "lecture" && <p className="annonce">Lecture…</p>}
      {etat.phase === "erreur" && <p className="alerte">{etat.raison}</p>}

      {enregistre !== null && (
        /*
          L'accusé de réception, donné à l'écran et pas par courriel.

          La plupart des déclarants n'ont pas d'adresse professionnelle — sur deux chantiers réels,
          10 personnes sur 11 partagent la même. Le numéro affiché est ce qu'ils retiennent et ce
          qu'ils prononcent ; leur pile est ce qu'ils rouvrent.
        */
        <p className="annonce">
          <strong>Remontée n° {enregistre.numero} enregistrée</strong> sur{" "}
          {enregistre.materielCode}
          {enregistre.materielDescription === null ? "" : ` — ${enregistre.materielDescription}`}.
          {" "}Elle est arrivée au SAV
          {enregistre.nPlus1Resolu === "fiche" && enregistre.nPlus1Libelle !== null
            ? `, avec copie à ${enregistre.nPlus1Libelle}.`
            : "."}
        </p>
      )}

      {etat.phase === "trouvee" && (
        <>
          <div className="fiche or__fiche">
            <h2>
              <code>{etat.machine.codeErp}</code>{" "}
              {etat.machine.description ?? etat.machine.type ?? "Machine"}
            </h2>

            {/* Une machine sortie du parc est RENDUE, jamais masquée : l'étiquette est collée
                dessus, et c'est souvent celle qui ne va pas. Mais le dire évite de saisir un
                ordre sur un engin vendu sans s'en apercevoir. */}
            {etat.machine.horsParc && (
              <p className="alerte-douce">
                Cette machine n'est plus au parc (sortie, inactive, ou date de sortie passée).
                Vérifiez l'étiquette avant de saisir.
              </p>
            )}

            {photos.length > 0 && (
              <div className="or__photos">
                {photos.map((url) => (
                  <img key={url} src={url} alt={`Machine ${etat.machine.codeErp}`} />
                ))}
              </div>
            )}
            {messagePhotos !== null && <p className="note">{messagePhotos}</p>}

            <dl className="fiche__grille">
              {CHAMPS.map(({ cle, libelle }) => {
                const valeur = etat.machine[cle];
                if (typeof valeur !== "string" || valeur === "") return null;
                return (
                  <div key={String(cle)}>
                    <dt>{libelle}</dt>
                    <dd>{valeur}</dd>
                  </div>
                );
              })}
            </dl>
          </div>

          <div className="or__saisie">
            <fieldset className="or__gravite">
              <legend>De quoi s'agit-il ?</legend>
              {types.length === 0 ? (
                <p className="alerte">
                  Aucun type d'incident n'est disponible : le référentiel n'a pas répondu. Rien ne
                  peut être remonté tant que cette liste est vide.
                </p>
              ) : (
                types.map((t) => (
                  <label key={t.code}>
                    <input
                      type="radio"
                      name="typeIncident"
                      checked={typeCode === t.code}
                      onChange={() => {
                        setTypeCode(t.code);
                      }}
                    />
                    <strong>{t.libelle}</strong>
                    {t.exemple === null ? null : <span className="note">{t.exemple}</span>}
                  </label>
                ))
              )}
            </fieldset>

            <label>
              <span>Ce qui ne va pas</span>
              <textarea
                value={constat}
                rows={4}
                placeholder="Ex. : fuite hydraulique sur le vérin de levage, perte de puissance."
                onChange={(e) => {
                  setConstat(e.target.value);
                }}
              />
            </label>

            {/*
              Où c'est arrivé : un chantier OU la case, jamais rien.

              Un mécanicien d'atelier n'est sur aucun chantier. Laisser la liste vide rendrait
              « pas de chantier » et « pas répondu » impossibles à distinguer — c'est la règle du
              produit sur les données manquantes, et elle vaut ici comme ailleurs.
            */}
            <label>
              <span>Où est-ce arrivé ?</span>
              <select
                value={chantierId}
                disabled={horsChantier}
                onChange={(e) => {
                  setChantierId(e.target.value);
                }}
              >
                <option value="">— choisir le chantier —</option>
                {chantiers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.apo} — {c.nom}
                  </option>
                ))}
              </select>
            </label>
            <label className="or__hors-chantier">
              <input
                type="checkbox"
                checked={horsChantier}
                onChange={(e) => {
                  setHorsChantier(e.target.checked);
                  if (e.target.checked) setChantierId("");
                }}
              />
              <span>À l'atelier, hors chantier</span>
            </label>

            <fieldset className="or__gravite">
              <legend>La machine est-elle immobilisée ?</legend>
              <label>
                <input
                  type="radio"
                  name="immobilisee"
                  checked={immobilisee === true}
                  onChange={() => {
                    setImmobilisee(true);
                  }}
                />
                <strong>Oui</strong>
                <span className="note">Elle ne peut plus travailler.</span>
              </label>
              <label>
                <input
                  type="radio"
                  name="immobilisee"
                  checked={immobilisee === false}
                  onChange={() => {
                    setImmobilisee(false);
                  }}
                />
                <strong>Non</strong>
                <span className="note">Elle travaille encore, même mal.</span>
              </label>
            </fieldset>

            {/*
              Le compteur au moment de l'incident (091, Chris du 21/09/2026 : « dans la remontée
              incident il faut demander KM ou compteur en même temps »).

              **Demandé, pas exigé.** Une panne qui immobilise une machine ne doit pas rester non
              signalée parce que le cadran est illisible ou que la personne n'est pas montée dans
              la cabine. Le bouton d'envoi ne le réclame donc pas.

              L'unité est à côté du nombre et non déduite de la machine : le parc ne dit nulle part
              si un engin compte en heures, et le déduire d'une immatriculation serait une règle
              inventée — 72 machines n'en ont pas, ce qui ne prouve rien sur leur cadran.
            */}
            <label>
              <span>Compteur (facultatif)</span>
              <span className="or__compteur">
                <input
                  type="text"
                  inputMode="decimal"
                  value={compteur}
                  placeholder="ex. 147852"
                  onChange={(e) => {
                    setCompteur(e.target.value);
                  }}
                />
                <select
                  value={compteurUnite}
                  onChange={(e) => {
                    setCompteurUnite(e.target.value === "heures" ? "heures" : "km");
                  }}
                >
                  <option value="km">km</option>
                  <option value="heures">heures</option>
                </select>
              </span>
            </label>

            {/*
              La photo du DÉGÂT — obligatoire, et distincte de celle de l'étiquette.

              `capture="environment"` ouvre l'appareil photo arrière sur un téléphone et un
              sélecteur de fichier sur un ordinateur : le même bouton sert les deux, comme pour le
              QR plus haut.
            */}
            <label>
              <span>Photo du dégât (obligatoire)</span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={(e) => {
                  setPhotosDegat([...(e.target.files ?? [])]);
                }}
              />
            </label>
            {photosDegat.length > 0 && (
              <p className="note">
                {photosDegat.length === 1
                  ? "1 photo jointe."
                  : `${String(photosDegat.length)} photos jointes.`}
              </p>
            )}

            {erreurEnvoi !== null && <p className="alerte">{erreurEnvoi}</p>}
            {ceQuiManque !== null && <p className="note">{ceQuiManque}</p>}

            <div className="fiche__actions">
              <button
                type="button"
                onClick={enregistrer}
                disabled={envoi || ceQuiManque !== null}
              >
                {envoi ? "Envoi…" : "Envoyer la remontée"}
              </button>
              <button
                type="button"
                className="lien"
                onClick={() => {
                  setEtat({ phase: "attente" });
                  setPhotos([]);
                  setCode("");
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </>
      )}

      <h2>Dernières remontées</h2>
      {ordres.length === 0 ? (
        <p className="note">Aucune remontée d'incident pour le moment.</p>
      ) : (
        <table className="equipe__table">
          <thead>
            <tr>
              <th>N°</th>
              <th>Machine</th>
              <th>Type</th>
              <th>Description</th>
              <th>Où</th>
              <th>En copie</th>
              <th>Déclaré par</th>
              <th>Le</th>
            </tr>
          </thead>
          <tbody>
            {ordres.map((o) => (
              <tr key={o.id}>
                <td>{o.numero}</td>
                <td>
                  <code>{o.materielCode}</code>
                  {o.materielDescription === null ? null : (
                    <>
                      <br />
                      <span className="note">{o.materielDescription}</span>
                    </>
                  )}
                </td>
                <td>
                  {types.find((t) => t.code === o.typeCode)?.libelle ?? o.typeCode ?? "—"}
                  {o.immobiliseeConstatee === true && (
                    <>
                      <br />
                      <span className="note">machine à l'arrêt</span>
                    </>
                  )}
                </td>
                <td>{o.constat}</td>
                <td>
                  {o.horsChantier
                    ? "Atelier"
                    : (chantiers.find((c) => c.id === o.chantierId)?.apo ?? "—")}
                </td>
                {/*
                  La copie au N+1, dite en clair quand elle n'a pas pu partir.

                  Un tableau qui ne montrerait que les copies réussies se lirait « tout va bien » —
                  c'est le pire mode de panne d'un écran d'alerte, et celui du 16/08.
                */}
                <td>
                  {o.nPlus1Resolu === "fiche" ? (
                    (o.nPlus1Libelle ?? "oui")
                  ) : o.nPlus1Resolu === "courriel" ? (
                    <span className="note">
                      {o.nPlus1Libelle ?? "encadrant"} — sans fiche, pas de pile
                    </span>
                  ) : (
                    <span className="alerte">
                      {o.nPlus1Libelle ?? "aucun N+1 au référentiel"}
                    </span>
                  )}
                </td>
                <td>{o.saisiParLibelle}</td>
                <td>{new Date(o.creeLe).toLocaleString("fr-FR")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
